import {Injectable} from '@angular/core';
import {
    NachdigalService,
    TaskData
} from '@nachdigal';
import {Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {cloneDeep, cloneDeepWith, isEqual, isNil, omitBy} from 'lodash';
import {ObjectHelper} from '../helper/object.helper';
import {ConvertOldTaskDataService} from './convert-old-task-data.service';
import {ConversionHelper} from '../helper/conversion.helper';

export const convertedToV2021Date = 'convertedToV2021Date'
@Injectable()
export class TaskDataService {

    public static readonly TASK_DATA_VERSION = '2024';

    private taskId: string;
    private taskData: TaskData;

    constructor(private readonly nachdigalService: NachdigalService,
                private readonly convertOldService: ConvertOldTaskDataService) {
    }

    private static getMandantName(data: Record<string, Record<string, Record<string, string>>>): string {
        const vorname: string = data.personenAngaben?.antragsteller?.vorname?.trim();
        const nachname: string = data.personenAngaben?.antragsteller?.nachname?.trim();
        if (vorname && nachname) {
            return `${nachname}, ${vorname}`;
        }
        if (vorname && !nachname) {
            return vorname;
        }
        if (nachname && !vorname) {
            return nachname;
        }
        return null;
    }

    private static setClientName(data: any): string {

        const mandantName = TaskDataService.getMandantName(data);
        if (mandantName) {
            return mandantName;
        }

        const unternehmen: string = data.unternehmenAngaben?.bezeichnung?.trim();
        if (unternehmen) {
            return unternehmen;
        }

        const firmenName: string = data.angabenZumUnternehmen?.juristischePerson?.firmenname?.trim();
        if (firmenName) {
            return firmenName;
        }

        const firmenNameKapGes: string = data.allgKapGesAngaben?.juristischePerson?.firmenname?.trim();
        if (firmenNameKapGes) {
            return firmenNameKapGes;
        }

        const firmenNameKapGAus: string = data.korperschaftAngaben?.juristischePerson?.firmenname?.trim();
        if (firmenNameKapGAus) {
            return firmenNameKapGAus;
        }

        return 'Mandantenname nicht bekannt';
    }

    public loadTaskData(taskId: string): Observable<TaskData> {
        this.taskId = taskId;
        this.taskData = {
            stammdaten: {}
        };

        return this.nachdigalService.getTaskData(taskId).pipe(
          tap((taskData: TaskData) => {
            this.taskData = taskData;
            //return taskData;
          })
        );
          /*.pipe(
            tap((taskData: TaskData) => {
                this.taskData = cloneDeepWith(taskData, value => {
                    if (value.nutzdaten) {
                        this.nachdigalService.getFrontendDocument(taskId).subscribe(doc => {
                            if (ConversionHelper.needsv20tov21Conversion(doc,taskData.metadaten)) {
                                value.nutzdaten = this.convertOldService.convertNutzdaten(doc.element_of_order.app_name, value.nutzdaten);
                                localStorage.setItem(convertedToV2021Date, new Date().toLocaleString());
                                console.log('nutzdaten were converted to', value.nutzdaten);
                            }
                        });
                    }
                    return value;
                });
            })
        );*/
    }

    public getNutzdaten(datatype?: string): any {
        // Falls der Task frisch angelegt wurde, ohne Nutzdaten-Objekt
        if (isNil(this.taskData.nutzdaten)) {
            this.taskData.nutzdaten = {};
        }
        const data = datatype ? this.taskData.nutzdaten[datatype] : this.taskData.nutzdaten;
        if (!data) {
            // Falls das Datenobjekt leer ist, dieses auf jeden Fall für die Komponenten vorinitialisieren,
            return {};
        }
        // Wir geben nur einen Klon zurück, damit der interne Speicher nicht überschrieben wird
        // Dies soll nur die PUT Methode durchführen
        return cloneDeep(data);
    }

    public putNutzdaten(datatype: string, data: unknown): Observable<unknown> {
        let forceSave = false;

        // kennzeichnen des Bereichs als "touched" -> der Anwender war mindestens einmal im Formularteil
        if (!this.taskData.metadaten.pages[datatype].touched) {
            this.taskData.metadaten.pages[datatype].touched = true;
            forceSave = true;
        }

        const itemKey = 'conversion-confirmation-date';
        if (localStorage.getItem(itemKey)) {
            forceSave = true;
            this.taskData.metadaten.conversion_confirmation_date = localStorage.getItem(itemKey);
            localStorage.removeItem(itemKey);
        }
        if(localStorage.getItem(convertedToV2021Date)){
          forceSave = true;
          this.taskData.metadaten.convertedTo21DataFormatAt = localStorage.getItem(convertedToV2021Date);
          localStorage.removeItem(convertedToV2021Date);
        }

        // remove all undefined and empty objects/properties
        const dataToPut = ObjectHelper.deepOmit(cloneDeep(data));

        if (!forceSave && isEqual(dataToPut, this.taskData.nutzdaten[datatype])) {
            // Wenn es keine Änderungen gibt, wird nicht gespeichert
            return of<unknown>({});
        }

        this.taskData.metadaten.task_data_version = TaskDataService.TASK_DATA_VERSION;

        this.taskData.nutzdaten[datatype] = dataToPut;
        this.taskData.nutzdaten = omitBy(this.taskData.nutzdaten, isNil);

        const clientName = TaskDataService.setClientName(this.taskData.nutzdaten);

        return this.nachdigalService.postTaskData(this.taskId, this.taskData, clientName).pipe(
            catchError(error => {
                // Erneutes speichern im Fehlerfall erzwingen
                this.taskData.metadaten.pages[datatype].touched = false;
                throw error;
            })
        );
    }

    public getStammdaten(): unknown {
        if (!this.taskData.stammdaten) {
            return null;
        }
        // Wir geben nur einen Klon zurück, damit der interne Speicher nicht überschrieben wird
        return cloneDeep(this.taskData.stammdaten);
    }

    public getPageMetadata(datatype: string): unknown {
        // Falls der Task frisch angelegt wurde, ohne Metadaten-Objekt
        if (isNil(this.taskData.metadaten)) {
            this.taskData.metadaten = {
                pages: {}
            };
        }
        // Anlegen eines Metadaten-Objekts falls noch nicht vorhanden
        if (!this.taskData.metadaten.pages[datatype]) {
            this.taskData.metadaten.pages[datatype] = {};
        }
        // Wir geben nur einen Klon zurück, damit der interne Speicher nicht überschrieben wird
        return cloneDeep(this.taskData.metadaten.pages[datatype]);
    }

}
